import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Grid3x3, List } from 'lucide-react';
import {
  CardLayout,
  DashboardContainer,
  MyProjectDropDown,
} from '../../components/ui/UtilsLayout';
import { iconsPath } from '../../utils/imagePath';

// Labels shown in dropdown
const TECH_TYPE_OPTIONS = [
  'All',
  'AR-SURFACE',
  'AR-WORLD',
  'AR-VPS',
  'AR-BODY-FOOT',
  'VR',
  // 'XR',
  'MR',
];
const SORT_OPTIONS = ['Most Recent', 'Recent', 'Previous'];

// Map display labels → API category values
const CATEGORY_MAP = {
  'AR-SURFACE': 'SURFACE',
  'AR-WORLD': 'WORLD',
  'AR-VPS': 'VPS',
  'AR-BODY-FOOT': 'FOOT',
  VR: 'VR',
  XR: 'XR',
  MR: 'MR',
};

const SORT_MAPPING = {
  'Most Recent': 'desc',
  Recent: 'desc',
  Previous: 'asc',
};

const MyProject = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const [filters, setFilters] = useState({
    selectedTech: 'All',
    selectedSort: 'Most Recent',
  });

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');

        let apiUrl = `${process.env.REACT_APP_BASE_URL}experience?page=1&limit=100`;
        apiUrl += `&sortBy=created_at&order=${SORT_MAPPING[filters.selectedSort]}`;

        // Convert dropdown label → backend value
        const category = CATEGORY_MAP[filters.selectedTech];
        if (category) {
          apiUrl += `&category=${category}`;
        }

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setProjects(response.data?.data || []);
        setFilteredProjects(response.data?.data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [filters]);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter((project) =>
        project.project_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProjects(filtered);
    }
  }, [searchQuery, projects]);

  return (
    <DashboardContainer>
      {/* Header */}
      <div className="flex flex-col gap-4">
        {filteredProjects.length > 0 && (
          <div className="font-semibold text-[20px]">
            {filteredProjects.length} Projects
          </div>
        )}

        {/* Filters and Search Row */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Filters */}
          <div className="flex gap-1 md:gap-4">
            {/* Tech Type Dropdown */}
            <MyProjectDropDown
              options={TECH_TYPE_OPTIONS}
              value={
                <div className="flex flex-row gap-1">
                  <span className="hidden md:block font-bold">Tech Type:</span>
                  <span>{filters.selectedTech}</span>
                </div>
              }
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, selectedTech: value }))
              }
            />

            {/* Sort By Dropdown */}
            <MyProjectDropDown
              options={SORT_OPTIONS}
              value={
                <div className="flex flex-row gap-1">
                  <span className="hidden md:block font-bold">Sort by:</span>
                  <span>{filters.selectedSort}</span>
                </div>
              }
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, selectedSort: value }))
              }
            />
          </div>

          {/* Right side - Search and View Toggle */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            {/* Search Bar */}
            <div className="relative flex-1 md:w-72">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#B8B8B8] w-5 h-5" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#444447] text-white pl-10 pr-4 py-1 rounded-[50px] border border-[#5C5C5C] focus:outline-none focus:border-gray-600 placeholder-text-[#B8B8B8];
]"
              />
            </div>

            {/* View Toggle Icons */}
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${
                viewMode === 'grid'
                  ? 'bg-[#31333A] text-white'
                  : 'bg-[#31333A] text-white'
              }`}
              aria-label="Grid view"
            >
              <img src={iconsPath?.projectFilter1} alt="" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg `}
              aria-label="List view"
            >
              <img src={iconsPath?.projectFilter2} alt="" />
            </button>
          </div>
        </div>
      </div>

      {/* Project List */}
      {loading ? (
        <div className="text-center py-8">Loading projects...</div>
      ) : (
        <CardLayout
          showAll
          id="my_projects"
          showName="project_name"
          cards={filteredProjects}
          viewMode={viewMode}
        />
      )}
    </DashboardContainer>
  );
};

export default MyProject;
